import { Component, inject, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { SearchStateService } from '../../../shared/services/search-state.service';
import { MCAppGatewayService } from '../../../../shared/services/mcappgateway/mcappgateway.service';
import { PostGetProviderDetailsRequest } from '../../../../shared/model/providerlocationsearch/PostGetProviderDetailRequest';
import { GetCareNowFormatService } from '../../../../get-care-now-feature/utilities/getcarenow-format.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../../environments/environment';
import { ProviderSearchDetailsLocationCardComponent } from '../provider-search-details-location-card/provider-search-details-location-card.component';
import { SearchMockService } from '../../../shared/services/search-mock.service';
import { SafeMethod } from '../../../../shared/decorators/safe-method.decorator';
import { ProviderSearchFilters } from '../../../../shared/model/providerlocationsearch/GetProviderSearchFilterResponse';
import { LocationCoordinates } from '../../../../shared/model/LocationCoordinates';
import { LocationSearchFilters } from '../../../../shared/model/providerlocationsearch/GetLocationSearchFilterResponse';

@Component({
  selector: 'app-provider-search-details-mobile',
  standalone: true,
  imports: [
    CommonModule, 
    MatButtonModule, 
    MatIconModule, 
    MatProgressSpinnerModule,
    ProviderSearchDetailsLocationCardComponent
  ],
  templateUrl: './provider-search-details.component.html',
  styleUrl: './provider-search-details.component.css'
})
export class ProviderSearchDetailsComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private mcAppGateway = inject(MCAppGatewayService);
  private searchState = inject(SearchStateService);
  private translate = inject(TranslateService);
  private mockService = inject(SearchMockService);
  private getCareNowFormatService: GetCareNowFormatService;

  @Input() isPlus: boolean = false;
  @Input() currentLocation: LocationCoordinates | null = null;
  @Input() locationDetails: any | null = null;
  @Input() locationDetailsProvider: any | null = null;

  eptID = "";
  providerDetails$ = this.searchState.providerDetails$;
  showSpinner$ = this.searchState.showSpinner$;
  providerSearchFilter$ = this.searchState.providerSearchFilters$;

  currentProviderFilters: ProviderSearchFilters | null = null;
  currentLocationFilters: LocationSearchFilters | null = null;
  showSpinner: boolean = true;
  details: any = null;
  waitTime: string = '';
  locationList: any[] = [];
  providerId: string = '';
  currentCityOrZip = "";
  locationCityOrZip = "";

  locale = "en-US";
  breadcrumb1 = "";
  breadcrumb2 = "";
  locationBreadcrumb1 = "";
  locationBreadcrumb2 = "";
  aboutLabel = "";
  affiliationsLabel = "";
  educationLabel = "";
  langLabel = "";
  locationsLabel = "";
  scheduleBtnText = "";


  constructor() {
    this.locale = sessionStorage.getItem('locale') || 'en-US'
    this.translate.use(this.locale);
    this.getCareNowFormatService = new GetCareNowFormatService(this.translate);
  }

  ngOnInit(): void {
    this.eptID = sessionStorage.getItem("eptID") ?? "";
    this.showSpinner = true;

    this.translate.get('PROVIDER_SEARCH').subscribe(translated => {
      this.breadcrumb1 = translated;
    });

    this.translate.get('LOCATION_SEARCH').subscribe(translated => {
      this.locationBreadcrumb1 = translated;
    });

    this.translate.get('RESULTS').subscribe(translated => {
      this.breadcrumb2 = translated
      this.locationBreadcrumb2 = translated
    });

    this.translate.get('PROVIDER_PROFILE.ABOUT').subscribe(translated => {
      this.aboutLabel = translated;
    });

    this.translate.get('PROVIDER_PROFILE.EDUCATION').subscribe(translated => {
      this.educationLabel = translated;
    });

    this.translate.get('PROVIDER_PROFILE.LOCATIONS').subscribe(translated => {
      this.locationsLabel = translated;
    });

    this.translate.get('FILTERS.LANGUAGES').subscribe(translated => {
      this.langLabel = translated;
    });

    this.translate.get('LOCATION_PROFILE.AFFILIATIONS').subscribe(translated => {
      this.affiliationsLabel = translated;
    });

    this.translate.get('SCHEDULE').subscribe(translated => {
      this.scheduleBtnText = translated;
    });


    // Additional initialization logic can go here
    this.showSpinner$.subscribe(show => {
      this.showSpinner = show;
    });

    this.searchState.providerDetails$.subscribe(details => {
      if (details) {
        // You can perform actions based on the provider details here
        console.log('Provider Details:', details);
        this.details = details;
        this.locationList = details.locations || [];

        this.waitTime = this.getCareNowFormatService.formatNextAvailability(this.details.nextAvailability); // update the next availability to display
      }
    });

    this.searchState.providerSearchFilters$.subscribe( filters => {
      if (filters) {
        this.currentProviderFilters = filters;
      }
    });

    this.searchState.locationSearchFilters$.subscribe( filters => {
      if (filters) {
        this.currentLocationFilters = filters;
      }
    });

    this.searchState.providerSearchQuery$.subscribe(query => {
      if (query) {
        this.currentCityOrZip = query.cityOrZip;
      }
    });

    this.searchState.locationSearchQuery$.subscribe(query => {
      if (query) {
        this.locationCityOrZip = query.cityOrZip;
      }
    });

    // Subscribe to provider details updates
    this.searchState.providerDetails$.subscribe(details => {
      if (details) {
        this.details = details;
        this.locationList = details.locations || [];
        
        // Format next availability if it exists
        if (details.nextAvailability) {
          this.waitTime = this.getCareNowFormatService.formatNextAvailability(details.nextAvailability);
        }
        
        this.showSpinner = false;
        this.searchState.setShowSpinner(false);
      }
    });

    // Subscribe to spinner state
    this.searchState.showSpinner$.subscribe(show => {
      this.showSpinner = show;
    });

    if(this.locationDetailsProvider) {
      // console.log(setinf)

      this.details = this.locationDetailsProvider;
      this.locationList = this.locationDetailsProvider.locations || [];

      // Format next availability if it exists
      if (this.locationDetailsProvider.nextAvailability) {
        this.waitTime = this.getCareNowFormatService.formatNextAvailability(this.locationDetailsProvider.nextAvailability);
      }
    }
  }

  @SafeMethod()
  loadProviderDetails(): void {
    this.showSpinner = true;
    this.searchState.setShowSpinner(true);

    // Use real API
    const request = new PostGetProviderDetailsRequest();
    request.providerID = this.providerId;

    // Add location if available
    const currentLocation = this.searchState.getCurrentLocation();
    if (currentLocation) {
      request.latitude = currentLocation.latitude;
      request.longitude = currentLocation.longitude;
    }

    this.mcAppGateway.PostGetProviderDetails(request).subscribe({
      next: (response) => {
        if (response) {
          console.log('Provider details loaded successfully:', response);
          this.searchState.setCurrentProviderDetails(response);
        } else {
          console.error('Empty response received from provider details API');
          this.showSpinner = false;
          this.searchState.setShowSpinner(false);
        }
      },
      error: (error) => {
        console.error('Error loading provider details:', error);
        alert('Unable to load provider details. Please try again.');
        this.showSpinner = false;
        this.searchState.setShowSpinner(false);
      }
    });
  }

  @SafeMethod()
  clearSearch(): void {
    
    this.searchState.setProviderSearchQuery({ nameOrSpecialty: "", cityOrZip: this.currentCityOrZip })
    this.searchState.clearProviderSearchResults();
    this.clearProviderFilters();
    this.searchState.setProviderSearchFilterToggle(false);
    this.searchState.clearCurrentProviderDetails()
  }

  @SafeMethod()
  clearProviderFilters(): void {
    if (this.currentProviderFilters) {
      // Clear all selections
      this.currentProviderFilters.specialtyList?.forEach(option => option.isSelected = false);
      this.currentProviderFilters.genderList?.forEach(option => option.isSelected = false);
      this.currentProviderFilters.affiliationList?.forEach(option => option.isSelected = false);
      this.currentProviderFilters.languageList?.forEach(option => option.isSelected = false);
      this.currentProviderFilters.onlineSchedulingList?.forEach(option => option.isSelected = false);

      this.searchState.setProviderSearchFilters(this.currentProviderFilters);
    }
  }

  @SafeMethod()
  returnToSearchResults(): void {
    this.searchState.clearCurrentProviderDetails();
  }

  // LocationDetailsProvider Breadcrumb navigation
  backToLocationSearch(): void {
    this.searchState.setLocationSearchQuery({
      facilityOrLocationName: '',
      cityOrZip: this.locationCityOrZip
    });
    this.searchState.clearLocationSearchResults();
    this.clearLocationFilters();
    this.searchState.clearCurrentLocationDetails();
    this.searchState.clearCurrentLocationDetailsProvider();
  }

  clearLocationFilters() {
    if (this.currentLocationFilters) {
      // Clear all selections
      this.currentLocationFilters.serviceList?.forEach(option => option.isSelected = false);
      this.currentLocationFilters.typeList?.forEach(option => option.isSelected = false);
 
      this.searchState.setLocationSearchFilters(this.currentLocationFilters);
    }
  }


  backToLocationSearchResults(): void {
    this.searchState.clearCurrentLocationDetails();
    this.searchState.clearCurrentLocationDetailsProvider();
  }

  backToLocationDetails(): void {
    this.searchState.clearCurrentLocationDetailsProvider();
  }

  @SafeMethod()
  onScheduleButtonClicked2(): void {
    if (this.details && this.details.providerEmrId && this.details.providerEmrId.length > 0) {
      const url = environment.web_open_scheduling_deep_link_base_url 
        + "?id=" + this.details.providerEmrId
        + "&vt=50317";
      console.log('Opening scheduling page:', url);
      window.location.href = url;
    } else {
      console.error('Provider EMR ID not available for scheduling');
      alert('Scheduling is not available for this provider.');
    }
  }

  onScheduleButtonClicked() {
    const isMobileLaunch = window.location.href.includes('mobile');

    if (this.details && this.details.providerEmrId && this.details.providerEmrId.length > 0) {

      if (this.eptID != null && this.eptID != ""){
        this.scheduleAuth(this.isPlus, this.details);
      }
      else {
        this.scheduleUnAuth(isMobileLaunch, this.details);  
      }
    }
  }

  scheduleAuth(isPlus: boolean | null, provider: any) {
    var deepLink = ''; 

    if (isPlus == null) {
      deepLink = environment.web_scheduling_deep_link_base_url;
    }
    else {
       deepLink = isPlus
        ? environment.mobile_scheduling_deep_link_base_url // For Plus users, use the mobile deep link
        : environment.epic_scheduling_deep_link_base_url; //  For non-Plus users, use the epic deep link
    }

    deepLink += "?workflow=NewProvider"

    deepLink += "&selProvId=" + provider.providerEmrId; 
      
    if (provider.departmentId != null && provider.departmentId != '') {
      deepLink += "&selDepId=" + provider.departmentId;
    }

    if (provider.specialtyId != null && provider.specialtyId != '') {
      deepLink += "&selSpecialtyId=" + provider.specialtyId;
    }

    var searchMethod = sessionStorage.getItem('providerSearchMethod') ?? 'unknown';
    
    if (searchMethod.length > 0) {  
      if (searchMethod === 'freeText') {
        deepLink += "&linksource=AUTHHMPLFRSPC";
      }
      else if (searchMethod === 'quickSearch') {
        deepLink += "&linksource=AUTHHMPLQSPC";
      }
    }

    window.location.href = deepLink;
  }

  scheduleUnAuth(isMobile: boolean, provider: any) {
    var deepLink = environment.web_open_scheduling_deep_link_base_url 
      + "?id=" + provider.providerEmrId;
    
    if (provider.departmentId != null && provider.departmentId != '') {
      deepLink += "&dept=" + provider.departmentId;
    }

    if (provider.specialtyId != null && provider.specialtyId != '') {
      deepLink += "&specialty=" + provider.specialtyId;
    }

    deepLink += "&linksource=PLUAFPPROFSCHED";
    
    window.location.href = deepLink;
  }


  @SafeMethod('')
  getProviderPhoto(): string {
    return this.details?.photo || this.details?.photoUrl || 'vudprovider.png';
  }

  @SafeMethod(false)
  hasEducation(): boolean {
    return this.details?.providerEducations && this.details.providerEducations.length > 0;
  }

  @SafeMethod(false)
  hasLocations(): boolean {
    return this.locationList && this.locationList.length > 0;
  }

  @SafeMethod(false)
  canSchedule(): boolean {
    return (this.details?.nextAvailability !== null || (this.details?.openScheduling != null && this.details?.openScheduling == true && this.details?.departmentId != null)) && this.details?.providerEmrId && this.details.providerEmrId.length > 0 && this.details.departmentId && this.details.departmentId.length > 0;
  }
}
